import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../components/layout/PageHeader";
import { Button, Col, Form, Input, Row, Select, Spin } from "antd";
import {
    getUserId,
    getUserSystemRoles,
    isCompanyAdmin,
    updateUser,
    updateCustomerUsers,
    useUserId,
    useUserMe,
    isTraCertAdmin,
    useUpdateCustomerUsers,
    useUserSystemRoles
} from "../hooks/user";
import { useHistory } from "react-router-dom";
import { getCompany, getCompanyMe, getGuarDocCompanies, useCompany } from "../hooks/company";
import { BackButton } from "../components/layout/BackButton";
import {  USER_STATUS_ACTIVE, USER_STATUS_DISABLED, USER_STATUS_NO_ACCOUNT, USER_STATUS_TEMP_DISABLED, } from "../constants/user";

import { list_of_company_types } from "../constants/company";
import { CompanyListItemDto, UserCompanyRoles, UserDto, UserRoleDto } from "../generated-api";

export function UserPage({ match }: any) {
    const { userId } = (match || {}).params || {};
    const { t } = useTranslation();
    const history = useHistory();
    const currentUser = useUserMe().data || {};
    const userResponse = useUserId();
    const rolesResponse = useUserSystemRoles();
    const isUserTraCertAdmin = isTraCertAdmin();
    const roles = rolesResponse.data || [];
    const userStatusUpdateResponse = useUpdateCustomerUsers();
    useEffect(() => {
        if (userId) {
            getUserId(userId, true).then((_user: UserDto) => {
                if (isCompanyAdmin()) {
                    getCompanyMe().then(res => {
                    }).catch(console.error);
                }
            }).catch(error => {
                console.error(error);
                history.push('/users');
            });
            getCompany().then().catch(console.error);
            if (!roles.length) {
                getUserSystemRoles().then().catch(console.error);
            }
        } else {
            history.push('/users');
        }
    }, [userId]);

    const companyResponse = useCompany();
    const companies = companyResponse.data || [] as CompanyListItemDto[];

    const user = { ...userResponse.data } as UserDto;

    const updateStatusDisabled = currentUser.id === userId;

    const onFinish = (values: any): void => {
        const { firstName, lastName, email, phone } = values;
        updateUser(userId, {
            firstName, lastName, email, phone, userCompaniesRoles: userCompaniesRoles
        }).then(() => {
            history.push('/users');
        }).catch(console.error);
    };

    const onCompanyUsersUpdate = (): void => {
        if (userCompaniesRoles && userCompaniesRoles.length > 0) {
            updateCustomerUsers(userId, { userCompaniesRoles: userCompaniesRoles })
            .then(() => {
                getUserId(userId).then().catch(console.error);
            }).catch(console.error);
        }
    };

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };

    const tempUserRoles: UserCompanyRoles [] = []
    if (user.id === userId ) {
        for (const type of list_of_company_types) {
            const userCompanyRoles = user.userCompaniesRoles?.find((item) => item.companyType === type.code);
            if (userCompanyRoles) {
                tempUserRoles.push(userCompanyRoles);
            } else {
                tempUserRoles.push({
                    companyId: undefined,
                    companyType: type.code,
                    roles: [],
                    status: 4,
                    companyName: ''
                })
            }
        }
    }
    const [userCompaniesRoles, setUserCompaniesRoles] = useState<UserCompanyRoles[]>(tempUserRoles);
    if(userCompaniesRoles.length === 0) {
        userCompaniesRoles.push(...tempUserRoles)
    }

    function getInitialStatus(companyType: 1 | 2): 1|3|4|undefined {
        if (user) {
            const status = userCompaniesRoles?.find((item) => item.companyType === companyType)?.status;
            if(status === USER_STATUS_DISABLED || status === USER_STATUS_TEMP_DISABLED) {
                return USER_STATUS_TEMP_DISABLED
            }
            else{
                return status
            }
        }
        return USER_STATUS_NO_ACCOUNT;
    }

    function getInitialCompanyValue(companyType: 1 | 2) {
        const company = userCompaniesRoles?.find((item) => item.companyType === companyType);
        if (company) {
            return company.companyId;
        }
        return '';

    }
    function getInitialUserRoles(companyType: 1 | 2) : string[] {
        const roles = userCompaniesRoles?.find((item) => item.companyType === companyType)?.roles;
        return roles?.map((item: UserRoleDto) => item.role) || [];
    }

    function isStatusNoAccount(companyType: 1 | 2): boolean {
        const status = userCompaniesRoles.find((item) => item.companyType === companyType)?.status;
        return status === USER_STATUS_NO_ACCOUNT;
    }

    function isStatusDisabled(companyType: 1 | 2): boolean {
        const initialStatus = tempUserRoles.find((item) => item.companyType === companyType)?.status;
        return initialStatus !== USER_STATUS_NO_ACCOUNT  && updateStatusDisabled
    }

    const onStatusChange = (commpanyCode: number, value: 1|2|3|4): void => {
        const index = userCompaniesRoles.findIndex((item) => item.companyType === commpanyCode);
        userCompaniesRoles[index].status = value;
        if (value === USER_STATUS_NO_ACCOUNT) {
            userCompaniesRoles[index].companyId = undefined;
            userCompaniesRoles[index].roles = [];
        }
        setUserCompaniesRoles([...userCompaniesRoles]);
    };

    const onCompanyChange = (commpanyCode: number, value: string): void => {
        const index = userCompaniesRoles.findIndex((item) => item.companyType === commpanyCode);
        userCompaniesRoles[index].companyId = value;
        userCompaniesRoles[index].companyName = '';
        setUserCompaniesRoles([...userCompaniesRoles]);
    };

    const onRolesChange = (commpanyCode: number, values: string[]): void => {
        if (values) {
            const index = userCompaniesRoles.findIndex((item) => item.companyType === commpanyCode);
            userCompaniesRoles[index].roles = values.map((item: string) => {
                return {
                    role: item
                }
            });
            setUserCompaniesRoles([...userCompaniesRoles]);
        }
    };

    const isLoading = userResponse.loading || rolesResponse.loading || userStatusUpdateResponse.loading;

    return (
        <Spin spinning={isLoading}>
            <PageHeader />
            <div className={'top-block accent5-background'}>
                <h1 className={'h1'}><BackButton backPath={'/users'} />{t('user.title')}</h1>
            </div>
            <div className={'subpage-content'}>
                <Form name="userStatus" key={'userStatus_' + userResponse.loading}
                    onFinish={onCompanyUsersUpdate}
                    autoComplete={'off'}
                    labelCol={{ span: 11 }}
                    validateTrigger={'onSubmit'}>
                    {list_of_company_types.filter(x => isUserTraCertAdmin || x.code === 1).map((companyType, index: number) =>
                        <div key={index}>
                            <h3 style={{ fontWeight: '500' }}>{companyType.name} {t('applicationAccount')}</h3>
                            <Row className={'row-offset'}  >
                                <Col span={24} md={12} xl={8}>
                                    <Form.Item label={t('user.status.title')} name={'status' + index}
                                    initialValue={getInitialStatus(companyType.code)}>
                                        <Select style={{ width: '100%' }} value={getInitialStatus(companyType.code)} disabled={isStatusDisabled(companyType.code)}
                                        onChange={event => onStatusChange(companyType.code, event)}>
                                            <Select.Option key={USER_STATUS_ACTIVE} value={USER_STATUS_ACTIVE}>
                                                {t('user.status.active')}
                                            </Select.Option>
                                            <Select.Option key={ USER_STATUS_TEMP_DISABLED} value={USER_STATUS_TEMP_DISABLED}>
                                                {t('user.status.disabled')}
                                            </Select.Option>
                                            <Select.Option key={USER_STATUS_NO_ACCOUNT} value={USER_STATUS_NO_ACCOUNT}>
                                                {t('user.status.noAccount')}
                                            </Select.Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={24} md={12} xl={8}>
                                    <Form.Item label={t('user.company')} name={'companyId' + index} rules={[{ required: !isStatusNoAccount(companyType.code) }]} initialValue={getInitialCompanyValue(companyType.code)}>
                                        <Select placeholder={t('user.company')} allowClear showSearch disabled={isStatusNoAccount(companyType.code) || isStatusDisabled(companyType.code)} value ={getInitialCompanyValue(companyType.code)}
                                            style={{ width: '100%' }} onChange={event => onCompanyChange(companyType.code, event)}
                                            filterOption={(input, option): any =>
                                                String((option || {}).children || '').toLowerCase().indexOf(String(input || '').toLowerCase()) >= 0
                                            }>
                                            {getGuarDocCompanies(companies, companyType.code).map((company: CompanyListItemDto) =>
                                                <Select.Option key={company.id} value={company.id}>
                                                    {company.name}
                                                </Select.Option>
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={24} md={12} xl={8}>
                                    <Form.Item label={t('user.roles')} name={'roles' + index} rules={[{ required: !isStatusNoAccount(companyType.code) }]} initialValue={getInitialUserRoles(companyType.code)}>
                                        <Select placeholder={t('user.roles')} allowClear style={{ width: '100%' }} disabled={isStatusNoAccount(companyType.code)} value={getInitialUserRoles(companyType.code)}
                                            mode={'multiple'} onChange={event => onRolesChange(companyType.code, event)}>
                                            {roles.map((role: string) =>
                                                <Select.Option key={role} value={role}>
                                                    {t('user.role.' + role)}
                                                </Select.Option>
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row></div>
                    )}
                    <Form.Item className={'text-right'}>
                        <Button type="primary" className={'accent5-background text-white w-100'} htmlType="submit">
                            {t('user.update')}
                        </Button>
                    </Form.Item>
                </Form>
                <Form name="user" key={'user_' + userResponse.loading}
                    onFinish={onFinish}
                    autoComplete={'off'}
                    labelCol={{ span: 11 }}
                    validateMessages={validateMessages}
                    initialValues={user}
                    validateTrigger={'onSubmit'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('user.username')}>
                                <Input value={(user || {}).username} readOnly />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('user.firstName')} name={'firstName'}
                                rules={[{ required: true }, { max: 256 }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('user.lastName')} name={'lastName'}
                                rules={[{ required: true }, { max: 256 }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('user.email')} name={'email'}
                                rules={[{ required: true }, { max: 256 }, { type: 'email' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12} xl={8}>
                            <Form.Item label={t('user.phone')} name={'phone'}
                                rules={[{ max: 50 }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item className={'text-right'}>
                        <Button type="primary" className={'accent5-background text-white w-100'} htmlType="submit">
                            {t('user.save')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </Spin>
    )
}
